import $ from 'jquery';

/**
 * @const {String} EV_NAMESPACE the namespace for the events stops event collission
 */
const EV_NAMESPACE = "ca-draggable";

/**
 * @const {String} BODY the CSS3 Sizzle namespace that represents the body 
 */
const BODY = "body";

/**
 * Lazy - Alias function to stop progagation on an event
 * 
 * @param {Event} e The event to stop propagation on
 */
const stop = (e) => e.stopPropagation();

/**
 * Lazy - Alias function to clone an object using jQuery
 * @param {{}} o The object to clone
 * 
 * @returns {{}} The cloned object
 */
const clone = (o) => $.extend({}, o);

export default class Draggable {
    constructor(upgradeElement) {
        this.element    = $(upgradeElement);

        this.dataObj    = {top: 0, left: 0};
        this.session    = clone(this.dataObj);
        this.start      = clone(this.dataObj);

        this.offset     = this.element.offset();

        this.element.attr("data-ca-dr-draggable", "true");
        this.element.on('mousedown touchstart', (e) => this.mouseClick(e));
        $(window).on('mouseup', (e) => this.mouseRelease(e));
    }

    /**
     * Getter to get the top offset of the element set to be draggable
     * 
     * @return {number} The top offset
     */
    get top() {
        return this.offset.top;
    }

    /**
     * Getter to get the left offset of the element set to be draggable
     * 
     * @return {number} The left offset
     */
    get left() {
        return this.offset.left;
    }

    /**
     * Gets the relative position based on the actual position and the offset
     * 
     * @param {{top: number, left: number}} position The position of the element to get the relative postion for 
     * 
     * @return {{top: number, left: number}} The relative position of the element 
     */
    adjust(position) {
        const _this = this;
        return {
            top: position.top - _this.top,
            left: position.left - _this.left
        }
    }

    /**
     * Gets the position information from and event and puts it in a standard format. 
     * 
     * @param {Event} e the event to get the positional information from
     * 
     * @return {{top: number, left: number}} The position object
     */
    eventToDataObject(e) {
      let touch = undefined;
      if (e.originalEvent.touches) { // Check for touch event data
        touch = e.originalEvent.touches[0]
      }
        return {
            top : e.pageY || touch.pageY,
            left: e.pageX || touch.pageX
        }
    }

    /**
     * Handles the event when the mouse is clicked and held on the element
     * 
     * This causes the class to start to track the mouse
     * 
     * @event start the start event is fired to say that it has started dragging
     * 
     * @param {Event} e the event object generated by the click event
     */
    mouseClick(e) {
        stop(e);
        this.session = this.adjust(this.eventToDataObject(e));

        this.trigger('start', [this.session]);

        $(BODY).on('mousemove touchmove', (e) => this.mouseMove(e));
        $(BODY).on('mouseup touchend', (e) => this.mouseRelease(e));
    }

    /**
     * Handles the mouse been relased this is classed as the user stops wanting to drag
     * 
     * Releases the mousemove and mouseup events i.e. stops tracking the mouse
     * 
     * @event stop the stop event is fired to say the user has stopped dragging
     * 
     * @param {Event} e the event object generated by the mouseup event
     */
    mouseRelease(e) {
        stop(e);
        $(BODY).off('mousemove');
        $(BODY).off('touchmove');
        $(BODY).off('mouseup');
        this.trigger('stop', this.session);
    }

    /**
     * Handles the mouse been moved, I.e the user is dragging
     * 
     * Calculates the current session and the offset from the current session to the last (i,e how much the mouse has moved)
     * 
     * @event dragging the dragging the event is fired when the user is moving the mouse _after_ the start event but before the stop event
     * 
     * @param {Event} e the event object generated by the mousemove event
     */
    mouseMove(e) {
      stop(e);
      const _this = this;
      const current = this.adjust(this.eventToDataObject(e));
        const movedBy = {
            top: _this.session.top - current.top,
            left: _this.session.left - current.left
        }

        this.session = {
            top: current.top,
            left: current.left
        }

        this.trigger('dragging', [this.session, movedBy]);
    }

    /**
     * A wrapper around $.trigger that formats the event name and triggers it on the correct element.
     * 
     * @param {String} eventname the name of the event to trigger
     * @param {[<*>]} args an array of arguments to pass to the handler 
     */
    trigger(eventname, args = []) {
        this.element.trigger(`${EV_NAMESPACE}.${eventname}`, args);
    }

    /**
     * A wrapper around $.on that formats the event name , and attaches the handler
     * 
     * @param {String} eventname the name of the event to listen for
     * @param {Function} handler the handler function called when that event name is triggered
     */
    on(eventname, handler) {
        this.element.on(`${EV_NAMESPACE}.${eventname}`, function(e) {
            let args = Array.from(arguments);
            handler.apply(this, args);
        });
    }
}